import { authHeaders, storageScope } from './auth.js';
import { isRequired } from './consent.js';
import { solve } from './session.js';
/**
 * The live chat with a person of the customer's team (ADR-0063).
 *
 * `/api/handoff` (rag-public.yaml) says what can be offered right now: a
 * ticket to connect, a choice of languages, or "leave a message". The
 * conversation itself runs over the WebSocket of websocket/live-chat.yaml.
 *
 * The resume token is kept in localStorage — first-party storage, which is
 * why this widget is Shadow DOM and not an iframe (ADR-0003, ADR-0019) — so a
 * reload or a dropped connection rejoins the same conversation for up to 5
 * minutes. Nothing here is a secret: the ticket is single-use and expires in
 * 60 seconds, the resume token only reopens this visitor's own conversation.
 */

const STORAGE_PREFIX = 'chatbot_live:';
const PING_EVERY_MS = 5 * 60 * 1000;
// Reconnecting past this would be past the service's own window.
const RESUME_WINDOW_MS = 5 * 60 * 1000;
const BACKOFF_MS = [1000, 2000, 4000, 8000, 15000];

function key(siteKey) {
  // Per person for the support assistant, like its thread (ADR-0074).
  return STORAGE_PREFIX + siteKey + storageScope();
}

/** The live conversation this visitor can rejoin, or null. */
export function storedLive(siteKey) {
  try {
    const value = JSON.parse(localStorage.getItem(key(siteKey)) || 'null');
    return value && value.conversationId && value.resumeToken ? value : null;
  } catch {
    return null;
  }
}

function storeLive(siteKey, value) {
  try {
    localStorage.setItem(key(siteKey), JSON.stringify(value));
  } catch {
    // Storage blocked: the conversation lives for this page only.
  }
}

export function forgetLive(siteKey) {
  try {
    localStorage.removeItem(key(siteKey));
  } catch {
    // Nothing stored.
  }
}

async function post(apiBase, path, session, body) {
  const response = await fetch(new URL(path, apiBase), {
    method: 'POST',
    credentials: 'omit',
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(session ? { 'X-Chat-Session': session } : {}) },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  // An authorization this visitor no longer has (ADR-0089): the panel asks again.
  isRequired(response.status, data);
  return { status: response.status, data };
}

/**
 * Asks for a person. Returns one of:
 * - { kind: 'connect', ticket, url, locale, threadId }
 * - { kind: 'choose', locales, threadId }
 * - { kind: 'unavailable', threadId }
 * - { kind: 'closed', opensAt, threadId } — outside the support hours
 * - { kind: 'open', status } — this thread is already with the team (409)
 * - { kind: 'session' } — the chat session was refused (401)
 * - { kind: 'error' }
 */
export async function requestHandoff(apiBase, siteKey, session, { threadId, visitorLocale, locale, reason }) {
  let result;
  try {
    result = await post(apiBase, '/api/handoff', session, {
      site_key: siteKey,
      visitor_locale: visitorLocale,
      reason,
      ...(threadId ? { thread_id: threadId } : {}),
      ...(locale ? { locale } : {}),
    });
  } catch {
    return { kind: 'error' };
  }
  const { status, data } = result;
  if (status === 401) return { kind: 'session' };
  if (status === 409) return { kind: 'open', status: data.conversation_status };
  if (status !== 200) return { kind: 'error' };
  if (data.result === 'connect') return { kind: 'connect', ticket: data.ticket, url: data.websocket_url, locale: data.locale, threadId: data.thread_id };
  if (data.result === 'choose_locale') return { kind: 'choose', locales: data.available_locales, threadId: data.thread_id };
  // Outside the support hours for that language (ADR-0065).
  if (data.result === 'closed') return { kind: 'closed', opensAt: data.opens_at || null, threadId: data.thread_id };
  return { kind: 'unavailable', threadId: data.thread_id };
}

/**
 * Whether a remembered thread is still with the team: 'waiting_advisor',
 * 'human', 'bot' (closed while the visitor was away), or null when the
 * answer could not be had (offline — try to rejoin anyway).
 */
export async function liveStatus(apiBase, siteKey, session, threadId) {
  try {
    const url = new URL('/api/handoff/status', apiBase);
    url.searchParams.set('site_key', siteKey);
    url.searchParams.set('thread_id', threadId);
    const response = await fetch(url, { credentials: 'omit', headers: { ...authHeaders(), ...(session ? { 'X-Chat-Session': session } : {}) } });
    if (response.status === 403) isRequired(403, await response.json().catch(() => null));
    if (!response.ok) return null;
    return (await response.json()).conversation_status || null;
  } catch {
    return null;
  }
}

/**
 * "Leave a message". Resolves to 'sent', 'invalid_email' (the address
 * cannot receive email), 'invalid_field:<key>' (an extra field required and
 * empty, or out of its type — ADR-0110), 'limit' (too many left: try later,
 * never with a new session), 'session' (refused: earn a new one) or
 * 'error'. A suspicious session is asked to solve a heavier challenge first
 * (428, ADR-0093): solved here, and the same message sent again with it.
 */
export async function leaveMessage(apiBase, siteKey, session, { threadId, name, email, message, visitorLocale, fields }) {
  const filled = Object.fromEntries(Object.entries(fields || {}).filter(([, value]) => typeof value === 'string' && value.trim() !== ''));
  const body = {
    site_key: siteKey,
    name,
    email,
    message,
    visitor_locale: visitorLocale,
    ...(threadId ? { thread_id: threadId } : {}),
    ...(Object.keys(filled).length ? { fields: filled } : {}),
    ...(visitorTimezone() ? { visitor_timezone: visitorTimezone() } : {}),
  };
  try {
    let { status, data } = await post(apiBase, '/api/handoff/message', session, body);
    if (status === 428 && data && data.challenge) {
      const proof = await solve(data.challenge);
      if (!proof) return 'error';
      ({ status, data } = await post(apiBase, '/api/handoff/message', session, { ...body, proof }));
    }
    if (status === 202) return 'sent';
    if (status === 422 && data && data.error === 'invalid_email') return 'invalid_email';
    if (status === 422 && data && data.error === 'invalid_field' && /^f\d{1,10}$/.test(data.field || '')) return `invalid_field:${data.field}`;
    if (status === 429) return 'limit';
    if (status === 401) return 'session';
    return 'error';
  } catch {
    return 'error';
  }
}

/**
 * The support assistant opens a ticket when nobody can attend (ADR-0078):
 * { id, url } or null. The client and the person come from its token.
 */
export async function openSupportTicket(apiBase, siteKey, session, { threadId, message }) {
  try {
    const { status, data } = await post(apiBase, '/api/support/ticket', session, {
      site_key: siteKey,
      message,
      ...(threadId ? { thread_id: threadId } : {}),
    });
    return status === 201 && data && data.ticket_id ? { id: data.ticket_id, url: data.url } : null;
  } catch {
    return null;
  }
}

function uuid() {
  if (crypto.randomUUID) return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * One live conversation over the WebSocket. `on` receives every frame from
 * the service by type, plus `drop` (reconnecting) and `lost` (could not
 * rejoin: the conversation is over for this visitor).
 */
export function liveConnection(siteKey, on) {
  let socket = null;
  let lastTyping = 0;
  let pinger = null;
  let renewal = null;
  let attempt = 0;
  let droppedAt = 0;
  let stopped = false;
  let saved = null;

  const send = (frame) => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(frame));
      return true;
    }
    return false;
  };

  function open(url) {
    const current = new WebSocket(url);
    socket = current;
    current.addEventListener('open', () => {
      attempt = 0;
      droppedAt = 0;
      // The first frame gets the ready frame (nothing can be pushed while
      // the service accepts the connection).
      current.send(JSON.stringify({ type: 'ping' }));
      clearInterval(pinger);
      pinger = setInterval(() => send({ type: 'ping' }), PING_EVERY_MS);
    });
    current.addEventListener('message', (event) => {
      let frame;
      try {
        frame = JSON.parse(event.data);
      } catch {
        return;
      }
      if (frame.type === 'session.ready') {
        if (frame.resume_token) {
          saved = { ...saved, conversationId: frame.conversation_id, resumeToken: frame.resume_token, locale: frame.locale };
          storeLive(siteKey, saved);
        }
        // Before API Gateway's 2-hour limit, rejoin quietly.
        clearTimeout(renewal);
        const renewIn = new Date(frame.reconnect_by).getTime() - Date.now();
        if (renewIn > 0) renewal = setTimeout(() => { if (socket === current) { socket = null; current.close(); resume(); } }, renewIn);
      }
      if (frame.type === 'conversation.closed') {
        stop();
      }
      (on[frame.type] || (() => {}))(frame);
    });
    current.addEventListener('close', () => {
      clearInterval(pinger);
      if (socket !== current || stopped) return;
      droppedAt = droppedAt || Date.now();
      if (Date.now() - droppedAt > RESUME_WINDOW_MS || !saved) {
        stop();
        (on.lost || (() => {}))();
        return;
      }
      (on.drop || (() => {}))();
      const delay = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)];
      attempt += 1;
      setTimeout(() => { if (!stopped) resume(); }, delay);
    });
  }

  function resume() {
    // The service rotates the resume token on every rejoin and sends the new
    // one in session.ready. Another tab of this visitor may have rejoined
    // since: its newer token is in storage, and ours no longer works.
    const latest = storedLive(siteKey);
    if (latest && latest.conversationId === saved.conversationId) {
      saved = { ...saved, resumeToken: latest.resumeToken };
    }
    open(`${saved.url}?conversation_id=${encodeURIComponent(saved.conversationId)}&resume_token=${encodeURIComponent(saved.resumeToken)}`);
  }

  function stop() {
    stopped = true;
    clearInterval(pinger);
    clearTimeout(renewal);
    forgetLive(siteKey);
  }

  return {
    /** First connection, with the ticket from /api/handoff. */
    start(url, ticket) {
      saved = { url };
      open(`${url}?ticket=${encodeURIComponent(ticket)}`);
    },
    /** Rejoin after a reload, with what was stored. */
    rejoin(stored) {
      saved = stored;
      resume();
    },
    message(text) {
      const id = uuid();
      return send({ type: 'visitor.message', client_message_id: id, text }) ? id : null;
    },
    end() {
      send({ type: 'visitor.close' });
    },
    /** Where to email the replies missed after leaving (ADR-0097); "" removes it. */
    email(address) {
      return send({ type: 'visitor.email', email: address });
    },
    /** The visitor is writing: at most one every 3 seconds (ADR-0066 §4). */
    typing() {
      const now = Date.now();
      if (now - lastTyping < 3000) return;
      lastTyping = now;
      send({ type: 'visitor.typing' });
    },
    close() {
      stop();
      if (socket) socket.close();
    },
  };
}

/** The browser's IANA time zone, or null (ADR-0066). */
export function visitorTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}
